import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { User, MapPin, Package, LogOut, Pencil, Trash2, Plus, Star } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { AddressForm } from "./Checkout";

export default function ProfilePage() {
  const { user, logout, refreshUser } = useAuth();
  const nav = useNavigate();
  const [tab, setTab] = useState("profile");
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [addresses, setAddresses] = useState([]);
  const [editing, setEditing] = useState(null); // null | 'new' | address
  const [busy, setBusy] = useState(false);

  const loadAddresses = () => api.get("/addresses").then(({ data }) => setAddresses(data.items)).catch(() => {});

  useEffect(() => {
    if (user === false) nav("/auth", { state: { from: "/profile" } });
    if (user) {
      setName(user.name || "");
      setPhone(user.phone || "");
      loadAddresses();
    }
  }, [user, nav]);

  if (!user) return null;

  const saveProfile = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.put("/profile", { name, phone });
      await refreshUser();
      toast.success("Profile updated");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const saveAddress = async (form) => {
    if (editing === "new") await api.post("/addresses", form);
    else await api.put(`/addresses/${editing.id}`, form);
    toast.success("Address saved");
    setEditing(null);
    loadAddresses();
  };

  const deleteAddress = async (id) => {
    await api.delete(`/addresses/${id}`);
    toast.success("Address removed");
    loadAddresses();
  };

  const TABS = [["profile", "Profile", User], ["addresses", "Saved Addresses", MapPin], ["orders-link", "My Orders", Package]];

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-8 py-10 lg:py-14" data-testid="profile-page">
      <h1 className="font-serif text-3xl sm:text-4xl font-bold text-stone-900 tracking-tight">My Account</h1>
      <p className="text-stone-500 mt-1">{user.email}</p>

      <div className="grid grid-cols-1 md:grid-cols-[220px_1fr] gap-8 mt-8">
        <div className="flex md:flex-col gap-2 overflow-x-auto no-scrollbar">
          {TABS.map(([k, l, Icon]) =>
            k === "orders-link" ? (
              <Link key={k} to="/orders" className="shrink-0 flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold text-stone-500 hover:bg-[#F5EFE6] transition-colors" data-testid="profile-tab-orders"><Icon size={15} /> {l}</Link>
            ) : (
              <button key={k} onClick={() => setTab(k)} className={`shrink-0 flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold transition-colors ${tab === k ? "bg-[#B45309]/10 text-[#B45309]" : "text-stone-500 hover:bg-[#F5EFE6]"}`} data-testid={`profile-tab-${k}`}>
                <Icon size={15} /> {l}
              </button>
            )
          )}
          <button onClick={async () => { await logout(); nav("/"); }} className="shrink-0 flex items-center gap-2.5 px-4 py-3 rounded-xl text-sm font-semibold text-red-700 hover:bg-red-50 transition-colors" data-testid="profile-logout">
            <LogOut size={15} /> Sign Out
          </button>
        </div>

        <div>
          {tab === "profile" && (
            <form onSubmit={saveProfile} className="bg-white rounded-2xl border border-[#E9E2D7] p-6 sm:p-8 max-w-lg" data-testid="profile-form">
              <h2 className="font-serif text-xl font-bold text-stone-900">Profile details</h2>
              <div className="space-y-4 mt-5">
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Full name</label>
                  <input value={name} onChange={(e) => setName(e.target.value)} className="mt-1.5 w-full h-11 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309]" data-testid="profile-name" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Email</label>
                  <input value={user.email} disabled className="mt-1.5 w-full h-11 px-4 rounded-xl border border-stone-100 bg-[#F5EFE6] text-sm text-stone-400" data-testid="profile-email" />
                </div>
                <div>
                  <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Phone</label>
                  <input value={phone} onChange={(e) => setPhone(e.target.value.replace(/\D/g, "").slice(0, 10))} placeholder="10-digit mobile" className="mt-1.5 w-full h-11 px-4 rounded-xl border border-stone-200 text-sm outline-none focus:border-[#B45309]" data-testid="profile-phone" />
                </div>
                <button disabled={busy} className="h-11 px-8 rounded-full bg-[#B45309] text-white text-sm font-bold hover:bg-[#92400E] transition-colors disabled:opacity-60" data-testid="profile-save">{busy ? "Saving…" : "Save Changes"}</button>
              </div>
            </form>
          )}

          {tab === "addresses" && (
            <div data-testid="addresses-section">
              {editing ? (
                <div className="bg-white rounded-2xl border border-[#E9E2D7] p-6 sm:p-8">
                  <h2 className="font-serif text-xl font-bold text-stone-900 mb-5">{editing === "new" ? "New address" : "Edit address"}</h2>
                  <AddressForm initial={editing === "new" ? undefined : editing} onSave={saveAddress} onCancel={() => setEditing(null)} />
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    {addresses.map((a) => (
                      <div key={a.id} className="p-5 rounded-2xl bg-white border border-[#E9E2D7]" data-testid={`address-card-${a.id}`}>
                        <div className="flex items-start justify-between">
                          <p className="font-bold text-stone-800 text-sm">{a.full_name}</p>
                          {a.is_default && <span className="flex items-center gap-1 px-2 py-0.5 rounded-full bg-[#78866B]/15 text-[#5c6b52] text-[10px] font-bold"><Star size={9} fill="currentColor" /> Default</span>}
                        </div>
                        <p className="text-sm text-stone-500 mt-1.5">{a.house}, {a.street}, {a.area}</p>
                        <p className="text-sm text-stone-500">{a.city}, {a.state} — {a.pincode}</p>
                        <p className="text-xs text-stone-400 mt-1">{a.mobile}</p>
                        <div className="flex gap-2 mt-3">
                          <button onClick={() => setEditing(a)} className="flex items-center gap-1.5 px-3 h-8 rounded-full border border-stone-200 text-xs font-semibold text-stone-600 hover:border-[#B45309] hover:text-[#B45309] transition-colors" data-testid={`address-edit-${a.id}`}><Pencil size={11} /> Edit</button>
                          <button onClick={() => deleteAddress(a.id)} className="flex items-center gap-1.5 px-3 h-8 rounded-full border border-stone-200 text-xs font-semibold text-stone-600 hover:border-red-400 hover:text-red-600 transition-colors" data-testid={`address-delete-${a.id}`}><Trash2 size={11} /> Delete</button>
                        </div>
                      </div>
                    ))}
                  </div>
                  <button onClick={() => setEditing("new")} className="mt-5 inline-flex items-center gap-2 px-6 h-11 rounded-full bg-stone-900 text-white text-sm font-bold hover:bg-[#B45309] transition-colors" data-testid="add-address-profile">
                    <Plus size={15} /> Add Address
                  </button>
                  {!addresses.length && <p className="text-sm text-stone-400 mt-4">No saved addresses yet — add one for faster checkout.</p>}
                </>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
